import {
  Box,
  Menu,
  Tooltip,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import {
  Close,
  Fireplace,
  MeetingRoom,
  RadioButtonChecked,
} from '@material-ui/icons'
import { DevicesFold, DoorFront } from '@mui/icons-material'
import camelcaseKeys from 'camelcase-keys'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Datagrid,
  Filter,
  GET_MANY,
  GetManyResult,
  ListProps,
  Pagination,
  ReferenceField,
  SelectInput,
  TextField,
  TextInput,
  sanitizeFetchType,
  useMutation,
  useNotify,
  useQuery,
  useQueryWithStore,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import List from '../../common/customized-ra-components/List'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
  DeviceStatusesPreview,
} from '../common/modules/ModuleStatus'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import Button from '../../common/customized-mui-components/Button'
import { DoorAttachEventRecurringBulkButton } from './door-list/DoorAttachEventRecurringBulkButton'
import { DoorDetachEventRecurringBulkButton } from './door-list/DoorDetachEventRecurringBulkButton'
import { DoorSetWorkingModeBulkButton } from './door-list/DoorSetWorkingModeBulkButton'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import QuickFilter from '../../common/QuickFilter'
import { DoorSetWorkingModeCompactButton } from './door-list/DoorSetWorkingModeCompactButton'
import { DoorTypes } from '../../../core/enum/DoorTypes'
import { DoorReleaseCompactButton } from './door-list/DoorReleaseCompactButton'
import { DoorDto } from '../../../core/dto/device/doors/door.dto'
import { DoorOpenBulkButton } from './door-list/DoorOpenBulkButton'
import { DeviceResetModulesBulkButton } from '../../common/DeviceResetModulesBulkButton'
import { useColorPalette } from '../../../core/devices/devices.utils'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'
import { UserSettingsDto } from '../../../core/dto/user-settings.dto'

const useStyles = makeStyles((theme) => ({
  compactStatus: {
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 34,
    cursor: 'default',
  },
  iconError: {
    color: theme.palette.error.dark,
    stroke: theme.palette.common.white,
    strokeWidth: 0.5,
  },
  icon: {
    padding: 4,
  },
  iconContainer: {
    position: 'relative',
    width: 34,
  },
  statusIcon: {
    position: 'absolute',
    left: 5,
    top: 5,
    fontSize: '24px !important',
  },
  crossIcon: {
    position: 'absolute',
    left: 0,
    top: 0,
    fontSize: 34,
    opacity: 0.8,
  },
  exclamationIcon: {
    position: 'absolute',
    left: 2.5,
    top: 0,
    fontSize: 34,
  },
}))

interface DoorListProps extends ListProps {
  readonly usePesistentFilters?: boolean
}

const DoorFilters = ({ usePesistentFilters, ...props }) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const { filterValues } = props
  const [mutate] = useMutation()

  const saveUserSettingsState = useCallback(
    async (settings: UserSettingsDto) => {
      await mutate({
        type: 'patchSettings',
        resource: ResourceName.CURRENT_USER,
        payload: settings,
      })
    },
    [mutate],
  )

  useEffect(() => {
    const settings = {
      viewState_Doors_Filters_DeviceCategoryId:
        filterValues?.categoryIdWithDescendants || undefined,
      viewState_Doors_Filters_TSCanId: filterValues?.tsCanId || undefined,
      viewState_Doors_Filters_EventActive:
        filterValues?.eventActive || undefined,
      viewState_Doors_Filters_Type: filterValues?.type || undefined,
    } as UserSettingsDto

    if (usePesistentFilters) saveUserSettingsState(settings)
  }, [filterValues, saveUserSettingsState, usePesistentFilters])

  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.palms.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_TS_CANS) && (
        <FilteredReferenceInput
          label="resources.doors.fields.tsCanId"
          source="tsCanId"
          reference={ResourceName.TS_CANS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            showFilter: true,
          }}
        />
      )}
      <SelectInput
        source="type"
        choices={Object.entries(DoorTypes).map(([value]) => ({
          id: value,
          name: `resources.enums.DoorTypes.${value}`,
        }))}
      />
      <QuickFilter source="eventActive" defaultValue />
    </Filter>
  )
}

const DoorGridExpand = ({ ...props }) => {
  const { record, serverDateTime } = props
  return (
    <DeviceStatusesPreview
      jsonStatus={record.jsonStatus}
      deviceType={DeviceTypes.DOORS}
      referenceDateTime={serverDateTime}
    />
  )
}

const DoorBulkActions = ({ ...props }) => {
  const { selectedIds } = props
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [mutate] = useMutation()
  const notify = useNotify()

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const [selectedDoors, setSelectedDoors] = useState<DoorDto[]>([])

  useEffect(() => {
    const fetchDoors = async () => {
      const selectedDoorResponse = (await mutate(
        {
          type: sanitizeFetchType(GET_MANY),
          resource: ResourceName.DOORS,
          payload: {
            ids: selectedIds,
          },
        },
        {
          returnPromise: true,
          onFailure: (err) => {
            notify(err?.message, 'error')
          },
        },
      )) as GetManyResult<DoorDto>
      if (selectedDoorResponse.data.length) {
        setSelectedDoors(selectedDoorResponse?.data)
      }
    }

    fetchDoors()
  }, [mutate, notify, selectedIds])

  const DoorOpenAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DoorOpenBulkButton
          selectedIds={selectedIds as number[]}
          selectedRecords={selectedDoors}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [selectedDoors, selectedIds],
  )

  const DoorWorkingModeAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DoorSetWorkingModeBulkButton
          selectedIds={selectedIds as number[]}
          selectedRecords={selectedDoors}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [selectedDoors, selectedIds],
  )

  const DoorWorkingModeRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DoorSetWorkingModeBulkButton
          selectedIds={selectedIds as number[]}
          selectedRecords={selectedDoors}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [selectedDoors, selectedIds],
  )

  const DoorAttachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DoorAttachEventRecurringBulkButton
          selectedIds={selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [selectedIds],
  )

  const DoorDetachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DoorDetachEventRecurringBulkButton
          selectedIds={selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [selectedIds],
  )

  const DoorResetModulesAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DeviceResetModulesBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          resource={ResourceName.DOORS}
        />
      )),
    [props.selectedIds],
  )

  return (
    <div>
      <Button
        onClick={handleClick}
        label="const.selectAction"
        variant="text"
        color="default"
        useSmallVersionBreakpoint={false}
      />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <DoorOpenAction />
        <DoorWorkingModeAction />
        <DoorWorkingModeRecurringAction />
        <DoorAttachEventRecurringAction />
        <DoorDetachEventRecurringAction />
        <DoorResetModulesAction />
      </Menu>
    </div>
  )
}

const CompactStatus = ({ ...props }) => {
  const { record: door } = props
  const classes = useStyles()
  const translate = useTranslate()

  const jsonStatus =
    door?.jsonStatus &&
    camelcaseKeys(JSON.parse(door?.jsonStatus), { deep: true })

  const DoorWorkingModeAction = () => {
    if (door.type !== DoorTypes.NO_READERS)
      return (
        <DoorSetWorkingModeCompactButton
          jsonStatus={jsonStatus}
          record={door}
        />
      )
    return <DoorReleaseCompactButton jsonStatus={jsonStatus} record={door} />
  }

  return (
    <Box
      className={classes.compactStatus}
      onClick={(event) => event.stopPropagation()}
    >
      {jsonStatus && (
        <Tooltip title={translate('resources.modules.fields.isDoorOpen')}>
          <div className={classes.iconContainer}>
            {jsonStatus?.isDoorOpen ? (
              <>
                <MeetingRoom
                  className={classes.icon}
                  style={{ position: 'relative', zIndex: 1 }}
                />
                <MeetingRoom
                  className={classes.icon}
                  style={{
                    position: 'absolute',
                    left: -2,
                    top: -2,
                    color: '#fff',
                    zIndex: 0,
                  }}
                />
              </>
            ) : (
              <DoorFront
                className={classes.icon}
                style={{
                  visibility:
                    jsonStatus?.isDoorOpen === false ? undefined : 'hidden',
                }}
              />
            )}
          </div>
        </Tooltip>
      )}
      {jsonStatus && <DoorWorkingModeAction />}
      {jsonStatus && (
        <Tooltip
          title={
            jsonStatus?.isControllerCaseOpen
              ? translate('resources.enums.isCoverOpened.open')
              : translate('resources.enums.isCoverOpened.closed')
          }
        >
          <DevicesFold
            className={classNames(
              jsonStatus?.isControllerCaseOpen && classes.iconError,
              classes.icon,
            )}
            style={{
              visibility:
                jsonStatus?.isControllerCaseOpen ||
                jsonStatus?.isControllerCaseOpen === false
                  ? undefined
                  : 'hidden',
            }}
          />
        </Tooltip>
      )}
      {jsonStatus && (
        <Tooltip title={translate('resources.modules.fields.fireAlarm')}>
          <div className={classes.iconContainer}>
            <Fireplace
              className={classes.icon}
              style={{
                visibility:
                  jsonStatus?.fireAlarmActive ||
                  jsonStatus?.fireAlarmActive === false
                    ? undefined
                    : 'hidden',
              }}
            />
            {jsonStatus?.fireAlarmActive === false ? (
              <Close fontSize="large" className={classes.crossIcon} />
            ) : undefined}
          </div>
        </Tooltip>
      )}
      {jsonStatus && (
        <Tooltip
          title={translate('resources.modules.fields.safeButtonPressed')}
        >
          <div className={classes.iconContainer}>
            <RadioButtonChecked
              className={classNames(
                jsonStatus?.safeButtonPressed && classes.iconError,
                classes.icon,
              )}
              style={{
                visibility:
                  jsonStatus?.safeButtonPressed ||
                  jsonStatus?.safeButtonPressed === false
                    ? undefined
                    : 'hidden',
              }}
            />
          </div>
        </Tooltip>
      )}
    </Box>
  )
}

export const DoorList = ({
  usePesistentFilters = false,
  ...props
}: DoorListProps) => {
  const theme = useTheme()
  const hasAuthority = useHasAuthority()
  const colorPalette = useColorPalette(theme)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const doorError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (doorError) return colorPalette(DeviceStatusCheck.ERROR)

    const doorWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.DOORS,
      serverDate?.serverDateTime,
    )

    if (doorWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  const { data: settings, loaded: settingsLoaded } = useQueryWithStore({
    type: 'getSettings',
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const filterDefaultValues = {
    categoryIdWithDescendants:
      settings?.viewState_Doors_Filters_DeviceCategoryId || undefined,
    tsCanId: settings?.viewState_Doors_Filters_TSCanId || undefined,
    eventActive: settings?.viewState_Doors_Filters_EventActive || undefined,
    type: settings?.viewState_Doors_Filters_Type || undefined,
  }

  return (
    <List
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      filters={
        settingsLoaded ? (
          <DoorFilters usePesistentFilters={usePesistentFilters} />
        ) : undefined
      }
      bulkActionButtons={<DoorBulkActions />}
      filterDefaultValues={
        settingsLoaded && usePesistentFilters ? filterDefaultValues : undefined
      }
      {...props}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        rowClick={(id) => `${ResourceName.DOORS}/${id}/show`}
        isRowExpandable={(record) =>
          record?.jsonStatus !== undefined && record?.jsonStatus !== null
        }
        expand={<DoorGridExpand serverDateTime={serverDate?.serverDateTime} />}
      >
        <TextField source="id" label="ID" />
        <TextField source="name" />
        {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link={false}
            sortBy="name"
          >
            <TextField source="hierarchyString" />
          </ReferenceField>
        )}
        <CompactStatus source="status" sortable={false} />
      </Datagrid>
    </List>
  )
}
